// @flow

import React, { useEffect, useState, useRef } from "react";
import type { Node } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { push } from "connected-react-router";
import { Helmet } from "react-helmet";
import { ToastContainer } from "react-toastify";
import { getCurrentPath, getRouteParams } from "@selectors";
import { getClientSettings } from "@modules/settings/selectors";
import { getClientContext } from "@modules/context/selectors";
import * as clientsSelectors from "@modules/client/selectors";
import type { State } from "@store/reducers";
import config from "@config";
import { withPermissions } from "@modules/account/components";
import {
  getPermissions,
  getUserType,
  getUser,
  lastThreeSuccessfulLogins,
} from "@modules/account/selectors";
import Sidebar from "../navigation/sidebar";
import { View, Loader, Button, AITeamChat } from "@components";
import AppHeader from "../navigation/app-header";

import "./dashboard.style.scss";
import {
  getUserId,
  isClientAdmin,
  hasDefaultClient,
  getIsImpersonateLogin,
  getDisplayName,
} from "@modules/account/selectors";

import clientsActionCreators from "@modules/client/actionCreator";
import settingsActions from "@modules/settings/actionCreator";
import type { Client } from "@modules/context/types";
import type { ProfileUser } from "@modules/account/types";
import LocalStorageService from "@services/LocalStorageService";
import { installNumericInputGuard } from "@utils/dom/numeric-input-guard";
import { getSystemMessagesReleaseNote } from "@modules/shared/selectors";
import { NewReleaseNoteModal } from "@modules/shared/components";
import moment from "moment";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { Settings } from "@modules/settings/types";
import type { ReleaseNote } from "@modules/shared/selectors";
import type { AITeamSettings } from "@modules/client/reducer/service/types";

type OwnProps = {
  title: string,
  currentUserId: number | null,
  isClientAdmin: boolean,
  hasDefaultClient: boolean,
  clientId?: number,
  hasPermission: *,
  hasAccess: *,
  extraProps: Object,
  hideSidebar?: boolean,
  hideNavigation?: boolean,
  currentUserType?: string,
  releaseNote: ReleaseNote | null,
  children?: Node, // Ma
};

type PropsFromState = {
  employeeId: ?number,
  location: string,
  breadCrumbs?: any,
  routeParams: Object,
  isImpersonateLogin: boolean,
  usersDisplayName: string,
  clientSettings: Object,
  client: Client | null,
  user: ProfileUser,
  lastThreeSuccessfulLogins: Array<string>,
  permissions: { [permission: string]: boolean } | null,
  aiTeamSettings: AITeamSettings | null,
};

type PropsFromDispatch = {
  navigate: Function,
  permissionId?: string,
  getSettings: typeof settingsActions.GetSettings,
  getServiceAbsenceManagementSettings: typeof clientsActionCreators.GetServiceAbsenceManagementSettings,
  getReleaseNotesDocument: typeof sharedActionCreators.GetReleaseNotesDocument,
};

type Props = OwnProps & PropsFromState & PropsFromDispatch & PropsFromLocalize & Settings;

export function DashboardLayout(props: Props) {
  const mainContent = useRef();
  mainContent.current && mainContent.current.addEventListener("scroll", onScroll);

  let { clientSettings, lastThreeSuccessfulLogins, releaseNote, title, isImpersonateLogin } = props;

  // TODO: Even better to store the whole release note as hash and compare it to the incoming one
  // if the hashes are different we got new version so we will display the release note message
  const localReleaseNoteCreated = LocalStorageService.getItem("releaseNoteCreatedAt");
  const releaseNoteCreatedAt = releaseNote?.createdAt;
  // Defining the clientID
  let clientId = props.clientId;
  if (props.hasDefaultClient) clientId = props.user.client.id;
  // Defining if the content is ready to be shown
  let showContent = false;
  if ((clientId || props.hasDefaultClient) && clientSettings) showContent = true;
  else if (!clientId) showContent = true;
  else if (!props.permissions) showContent = false;

  const [isChatToggled, setIsChatToggled] = useState(false);
  const [enableScrollToTopButton, setEnableScrollToTopButton] = useState(false);

  const [isCollapsed, setIsCollapsed] = useState(
    LocalStorageService.getItem("sidebarIsCollapsed") || false,
  );

  const [showReleaseNote, setShowReleaseNote] = useState();

  useEffect(() => {
    if (clientId) {
      props.getServiceAbsenceManagementSettings({
        clientId: clientId,
      });
      props.getSettings({
        clientId: clientId,
      });
    }
  }, [clientId]);

  useEffect(() => {
    if (releaseNote && releaseNoteCreatedAt && localReleaseNoteCreated !== releaseNoteCreatedAt) {
      LocalStorageService.setItem("releaseNoteCreatedAt", releaseNoteCreatedAt);
      setShowReleaseNote(shouldDisplayNewReleaseNotes());
    }
  }, [releaseNote]);

  useEffect(() => {
    // $FlowIgnore
    document.documentElement.style.setProperty(
      "--side-panel-top",
      isImpersonateLogin ? "94px" : "67px",
    );
  }, [isImpersonateLogin]);

  useEffect(() => {
    installNumericInputGuard();
  }, []);

  function onScroll(event: SyntheticEvent<EventTarget>) {
    // $FlowIgnore
    setEnableScrollToTopButton(!!(event.target.scrollTop > 100));
  }

  function scrollToTop() {
    const mainContent = document.querySelector("#main-content");
    if (mainContent) {
      //$FlowIgnore
      mainContent.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  function navigate(path: string) {
    props.navigate(path);
  }

  function toggleSidebarHandler() {
    LocalStorageService.setItem("sidebarIsCollapsed", !isCollapsed);

    setIsCollapsed(!isCollapsed);
  }

  function hideNewReleaseNotes() {
    setShowReleaseNote(false);
  }

  function shouldDisplayNewReleaseNotes() {
    if (!releaseNote) return false;
    const lastThreeSuccessfulLogins = props.lastThreeSuccessfulLogins || [];
    let show = false;

    const releaseNoteDate = moment(localReleaseNoteCreated);

    // define last successful login time
    const now = new Date();
    const _30sec = 30000; // in milliseconds
    const zeroKey = new Date(lastThreeSuccessfulLogins[0]);

    // if last login is made in the range of last 30sec, should use to the previous successful login
    const lastLoginDate = moment(
      now.getTime() - zeroKey.getTime() < _30sec
        ? lastThreeSuccessfulLogins[1]
        : lastThreeSuccessfulLogins[0],
    );

    // If the release date is after last successful login, then show the message
    if (releaseNoteDate.isAfter(lastLoginDate)) {
      show = true;
    }

    return show;
  }

  function downloadReleaseNotes() {
    releaseNote &&
      props.getReleaseNotesDocument({
        fileName: `${props.t("VERSION_CONTROL_NOTES_TITLE")} ${releaseNote.version || "??"}.pdf`,
      });
  }

  function toggleChat(isToggled) {
    setIsChatToggled(isChatToggled);
  }

  return (
    <View flex={1} justifyContent="space-between" className={[isCollapsed && "collapsed"]}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{title}</title>
      </Helmet>
      {!!isImpersonateLogin && (
        <View className="impersonate-notification">
          {props.t("IMPERSONATION_NOTIFICATION", {
            name: props.usersDisplayName,
          })}
        </View>
      )}
      <View direction="row">
        {(showContent && (
          <View
            key={JSON.stringify(`${isChatToggled}-${isCollapsed}`)}
            id="dashboard-layout"
            flex="1"
            overflow="hidden"
            direction="row"
          >
            <Sidebar
              navigate={navigate}
              currentPath={props.location}
              permissions={props.permissions}
              clientSettings={props.clientSettings}
              clientId={clientId}
              employeeId={props.employeeId}
              user={props.user}
              isCollapsed={isCollapsed}
              toggleSidebar={toggleSidebarHandler}
            />

            <View flex={1} id="content">
              <AppHeader
                navigate={navigate}
                currentPath={props.location}
                breadCrumbs={props.breadCrumbs}
                toggleSidebar={toggleSidebarHandler}
                isCollapsed={isCollapsed}
              />
              <View
                ref={mainContent}
                id="main-content"
                className={[isCollapsed && "collapsed"]}
                padding={(6, 0, 6, 6)}
                gap={6}
                flex={1}
                name={"MainContent"}
                onScroll={onScroll}
              >
                {props.children}

                <span
                  onClick={scrollToTop}
                  className={`scroll-to-top d-print-none material-icons-outlined ${
                    enableScrollToTopButton ? "show" : ""
                  }`}
                >
                  expand_less
                </span>
              </View>
            </View>
            <ToastContainer
              position="bottom-left"
              type="default"
              autoClose={config.toasterShowTime}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              pauseOnHover
            />
          </View>
        )) || <Loader />}
        <AITeamChat toggleChat={toggleChat} aiTeamSettings={props.aiTeamSettings} />
        {showReleaseNote && (
          <NewReleaseNoteModal
            releaseNote={releaseNote}
            onClose={hideNewReleaseNotes}
            onClick={downloadReleaseNotes}
          />
        )}
      </View>
    </View>
  );
}

const LocalisedDashboardLayout = localize()(DashboardLayout);

const mapStateToProps = (state: State, props: Object): PropsFromState => {
  const routeParams = getRouteParams(props.extraProps) || {};
  const user = getUser(state);
  return {
    routeParams,
    employeeId: routeParams.employeeId,
    clientId: routeParams.clientId,
    location: getCurrentPath(state),
    currentUserId: getUserId(state),
    isClientAdmin: isClientAdmin(state),
    hasDefaultClient: hasDefaultClient(state),
    isImpersonateLogin: getIsImpersonateLogin(state),
    usersDisplayName: getDisplayName(state),
    client: getClientContext(state),
    permissions: getPermissions(state),
    clientSettings: getClientSettings(state),
    currentUserType: getUserType(state),
    aiTeamSettings: clientsSelectors.getClientAITeamSettings(state),
    user,
    releaseNote: getSystemMessagesReleaseNote(state),
    lastThreeSuccessfulLogins: lastThreeSuccessfulLogins(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getSettings: settingsActions.GetSettings,
  getServiceAbsenceManagementSettings: clientsActionCreators.GetServiceAbsenceManagementSettings,
  navigate: path => push(path),
  getReleaseNotesDocument: sharedActionCreators.GetReleaseNotesDocument,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions({}),
)(LocalisedDashboardLayout);
